// StudyTimerPage.jsx
import React, { useState, useEffect } from 'react';
import { PlayIcon, PauseIcon, ArrowPathIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';
import { ClockIcon } from '@heroicons/react/24/outline'; // Add this line

const StudyTimerPage = () => {
  const [minutes, setMinutes] = useState(25);
  const [seconds, setSeconds] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [isBreak, setIsBreak] = useState(false); // true for break, false for focus
  const [focusDuration, setFocusDuration] = useState(25);
  const [breakDuration, setBreakDuration] = useState(5);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    let interval = null;
    if (isActive) {
      interval = setInterval(() => {
        if (seconds === 0) {
          if (minutes === 0) {
            // Timer ends
            clearInterval(interval);
            setIsActive(false);
            // Switch between focus and break
            if (!isBreak) {
              alert('Time for a break!');
              setMinutes(breakDuration);
              setSeconds(0);
              setIsBreak(true);
            } else {
              alert('Break is over! Time to focus!');
              setMinutes(focusDuration);
              setSeconds(0);
              setIsBreak(false);
            }
            // Optionally, auto-start next phase
            // setIsActive(true);
          } else {
            setMinutes(minutes - 1);
            setSeconds(59);
          }
        } else {
          setSeconds(seconds - 1);
        }
      }, 1000);
    } else if (!isActive && seconds !== 0 && interval) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [isActive, minutes, seconds, isBreak, focusDuration, breakDuration]);

  const toggle = () => {
    setIsActive(!isActive);
  };

  const reset = () => {
    setIsActive(false);
    setIsBreak(false);
    setMinutes(focusDuration);
    setSeconds(0);
  };

  const formatTime = (num) => num < 10 ? `0${num}` : num;

  const handleFocusDurationChange = (e) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value > 0) {
      setFocusDuration(value);
      if (!isBreak) setMinutes(value); // Update current timer if in focus mode
    }
  };

  const handleBreakDurationChange = (e) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value > 0) {
      setBreakDuration(value);
      if (isBreak) setMinutes(value); // Update current timer if in break mode
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-200 to-purple-300 p-8 flex items-center justify-center">
      <div className="bg-white rounded-3xl p-8 shadow-2xl border-b-8 border-indigo-600 transition-all duration-300 hover:shadow-3xl hover:scale-[1.01] w-full max-w-md text-center">
        <h1 className="text-4xl md:text-5xl font-extrabold text-indigo-800 mb-6 flex items-center justify-center">
          <ClockIcon className="w-10 h-10 mr-4 text-indigo-600" />
          Study Timer
        </h1>

        <div className="text-7xl md:text-8xl font-mono font-bold text-indigo-900 mb-8 bg-indigo-50 px-6 py-8 rounded-2xl shadow-inner-lg border border-indigo-200">
          {formatTime(minutes)}:{formatTime(seconds)}
        </div>

        <p className={`text-xl font-semibold mb-6 ${isBreak ? 'text-purple-700' : 'text-indigo-700'}`}>
          {isBreak ? 'Break Time!' : 'Focus Time!'}
        </p>

        <div className="flex justify-center space-x-4 mb-6">
          <button
            onClick={toggle}
            className={`p-4 rounded-full shadow-lg transition-all duration-300 transform hover:scale-110
              ${isActive ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'} text-white`}
          >
            {isActive ? <PauseIcon className="w-8 h-8" /> : <PlayIcon className="w-8 h-8" />}
          </button>
          <button
            onClick={reset}
            className="p-4 bg-gray-300 rounded-full shadow-lg hover:bg-gray-400 text-gray-800 transition-all duration-300 transform hover:scale-110"
          >
            <ArrowPathIcon className="w-8 h-8" />
          </button>
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="p-4 bg-blue-300 rounded-full shadow-lg hover:bg-blue-400 text-blue-800 transition-all duration-300 transform hover:scale-110"
          >
            <Cog6ToothIcon className="w-8 h-8" />
          </button>
        </div>

        {showSettings && (
          <div className="mt-8 p-6 bg-indigo-50 rounded-xl shadow-inner border border-indigo-200">
            <h3 className="text-xl font-bold text-indigo-700 mb-4">Timer Settings</h3>
            <div className="flex flex-col space-y-4">
              <div className="flex justify-between items-center">
                <label htmlFor="focus-duration" className="text-indigo-800 font-semibold">Focus Duration (minutes):</label>
                <input
                  type="number"
                  id="focus-duration"
                  value={focusDuration}
                  onChange={handleFocusDurationChange}
                  className="w-24 p-2 border border-indigo-300 rounded-md text-center text-indigo-900 font-medium focus:ring-2 focus:ring-indigo-400"
                  min="1"
                />
              </div>
              <div className="flex justify-between items-center">
                <label htmlFor="break-duration" className="text-indigo-800 font-semibold">Break Duration (minutes):</label>
                <input
                  type="number"
                  id="break-duration"
                  value={breakDuration}
                  onChange={handleBreakDurationChange}
                  className="w-24 p-2 border border-indigo-300 rounded-md text-center text-indigo-900 font-medium focus:ring-2 focus:ring-indigo-400"
                  min="1"
                />
              </div>
            </div>
          </div>
        )}

        <p className="mt-8 text-gray-600 text-sm italic">
          "Focus on your goals, not your fears."
        </p>
      </div>
    </div>
  );
};

export default StudyTimerPage;